import React, { useState, useEffect, useRef } from "react";
import BackgroundComponent from "./components/BackgroundComponent";
import OMMComponent from "./components/OMMComponent";
import AboutMeComponent from "./components/AboutMeComponent";
import ConditionsComponent from "./components/ConditionsComponent";
import BottomBorder from "./components/BottomBorder";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import { useSwipeable } from "react-swipeable";
import { useMediaQuery } from "@mui/material";
import "./App.css";

const sections = ["omm", "about", "conditions"];

function App() {
  const [activeSection, setActiveSection] = useState("omm");
  const [pendingSection, setPendingSection] = useState("omm"); // ✅ staging state
  const [bottomMinHeight, setBottomMinHeight] = useState("50px");
  const [direction, setDirection] = useState("left");

  const currentIndexRef = useRef(0);

  const goToSection = (index) => {
    if (index >= 0 && index < sections.length) {
      const newDirection = index > currentIndexRef.current ? "left" : "right";
      setDirection(newDirection);
      currentIndexRef.current = index;
      setPendingSection(sections[index]);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setActiveSection(pendingSection);
    }, 0);
    return () => clearTimeout(timeout);
  }, [pendingSection]);

  const handlers = useSwipeable({
    onSwipedLeft: () => goToSection(currentIndexRef.current + 1),
    onSwipedRight: () => goToSection(currentIndexRef.current - 1),
    trackMouse: true,
  });

  const isMobile = useMediaQuery("(max-width:399px)");

  useEffect(() => {
    const updateHeight = () => {
      const w = window.innerWidth;
      if (w < 500) {
        setBottomMinHeight("60vh");
      } else if (w < 550) {
        setBottomMinHeight("67vh");
      } else if (w < 600) {
        setBottomMinHeight("70vh");
      } else if (w < 700) {
        setBottomMinHeight("75vh");
      } else if (w < 750) {
        setBottomMinHeight("80vh");
      } else {
        setBottomMinHeight("80vh");
      }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const renderSection = () => {
    switch (activeSection) {
      case "omm":
        return <OMMComponent />;
      case "about":
        return <AboutMeComponent />;
      case "conditions":
        return <ConditionsComponent />;
      default:
        return <OMMComponent />;
    }
  };

  return (
    <>
      <main className="page">
        <div className="half top">
          <BackgroundComponent
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            currentIndex={currentIndexRef.current}
          />
        </div>

        <div
          className="half bottom"
          {...handlers}
          style={{ position: "relative", minHeight: bottomMinHeight }}
        >
          {/* Pagination Dots */}
          <div
            style={{
              position: "absolute",
              top: 10,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "6px",
              zIndex: 10,
            }}
          >
            {sections.map((sec, i) => (
              <button
                key={sec}
                onClick={() => goToSection(i)}
                aria-label={`Show ${sec === "omm" ? "osteopathic manipulative medicine" : sec} section`}
                aria-current={activeSection === sec ? "true" : undefined}
                style={{
                  width: "6px",
                  height: "6px",
                  padding: 0,
                  border: 0,
                  borderRadius: "50%",
                  backgroundColor: activeSection === sec ? "white" : "gray",
                  cursor: "pointer",
                  transition: "background-color 0.3s ease",
                }}
              />
            ))}
          </div>

          <div className="slide-container" style={{ height: "100%" }}>
            <SwitchTransition mode="out-in">
              <CSSTransition
                key={activeSection}
                timeout={300}
                classNames={`slide-${direction}`}
                unmountOnExit
              >
                <div className="section-wrapper" style={{ height: "100%" }}>
                  {renderSection()}
                </div>
              </CSSTransition>
            </SwitchTransition>
          </div>

          <div className="bottom-border">
            <BottomBorder />
          </div>

          {!isMobile && (
            <div
              className="nav-arrows"
              style={{
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                padding: "0 12px",
                zIndex: 0,
              }}
            >
              <div style={{ flex: 1 }}>
                {currentIndexRef.current > 0 && (
                  <button
                    onClick={() => goToSection(currentIndexRef.current - 1)}
                    aria-label="Show previous section"
                    style={{
                      fontSize: "1.25rem",
                      background: "none",
                      border: "none",
                      color: "white",
                      cursor: "pointer",
                    }}
                    onMouseDown={(e) => (e.currentTarget.style.color = "#999")}
                    onMouseUp={(e) => (e.currentTarget.style.color = "white")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "white")}
                  >
                    ❮
                  </button>
                )}
              </div>
              <div style={{ flex: 1, textAlign: "right" }}>
                {currentIndexRef.current < sections.length - 1 && (
                  <button
                    onClick={() => goToSection(currentIndexRef.current + 1)}
                    aria-label="Show next section"
                    style={{
                      fontSize: "1.25rem",
                      background: "none",
                      border: "none",
                      color: "white",
                      cursor: "pointer",
                    }}
                    onMouseDown={(e) => (e.currentTarget.style.color = "#999")}
                    onMouseUp={(e) => (e.currentTarget.style.color = "white")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "white")}
                  >
                    ❯
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
}

export default App;
